import React, { useState, useEffect, useRef, useCallback } from "react";
import "./CyberBike.css";

const CyberBike = ({ onBack, isPaused }) => {
    const canvasRef = useRef(null);
    const [score, setScore] = useState(0);
    const [gameOver, setGameOver] = useState(false);
    const [gameStarted, setGameStarted] = useState(false);
    const [isBoost, setIsBoost] = useState(false);
    const touchStartRef = useRef({ x: 0, y: 0, active: false });

    const CAN_W = 400;
    const CAN_H = 600;
    
    // Bike dimensions
    const BW = 30;
    const BH = 70;

    const game = useRef({
        player: { x: CAN_W/2 - BW/2, y: CAN_H - 150, targetX: CAN_W/2 - BW/2 },
        obstacles: [],
        lastSpawn: 0,
        lastFrame: 0,
        speed: 6,
        roadOffset: 0
    });

    const spawnObstacle = () => {
        const type = Math.random() > 0.4 ? 'BARRIER' : 'POLICE';
        const lane = Math.floor(Math.random() * 3);
        const x = 50 + lane * 100 + (100 - BW) / 2;
        const speed = game.current.speed + (type === 'POLICE' ? 2 : 0);
        return { x, y: -150, type, color: type === 'POLICE' ? '#3b82f6' : '#f43f5e', speed: Math.max(4, speed) };
    };

    const update = useCallback((time) => {
        if (!gameStarted || gameOver) return;
        if (isPaused) {
            game.current.lastFrame = time;
            requestAnimationFrame(update);
            return;
        }
        const dt = (time - game.current.lastFrame) / 16;
        game.current.lastFrame = time;

        game.current.speed += 0.002 * dt;
        game.current.player.x += (game.current.player.targetX - game.current.player.x) * 0.25 * dt;
        game.current.roadOffset = (game.current.roadOffset + game.current.speed * dt) % 100;

        const spawnDelay = Math.max(500, 1500 - game.current.speed * 80);
        if (time - game.current.lastSpawn > spawnDelay) {
            game.current.obstacles.push(spawnObstacle());
            game.current.lastSpawn = time;
        }

        let crashed = false;
        game.current.obstacles.forEach(obs => {
            obs.y += obs.speed * dt;
            
            // Precise hitboxes
            const px = game.current.player.x + 5;
            const py = game.current.player.y + 5;
            const pw = BW - 10;
            const ph = BH - 10;

            const ox = obs.x + 5;
            const oy = obs.y + 5;
            const ow = BW + 10; // Obstacles are slightly wider
            const oh = BH - 10;

            if (px < ox + ow && px + pw > ox && py < oy + oh && py + ph > oy) {
                crashed = true;
            }
        });

        if (crashed) {
            setGameOver(true);
            return;
        }

        game.current.obstacles = game.current.obstacles.filter(o => o.y < CAN_H + 200);
        setScore(s => s + game.current.speed * dt * 0.1);
        render();
        requestAnimationFrame(update);
    }, [gameStarted, gameOver, isPaused]);

    const render = () => {
        const ctx = canvasRef.current?.getContext("2d");
        if (!ctx) return;
        
        // Background - Dark Cyber Road
        ctx.fillStyle = "#0f172a";
        ctx.fillRect(0, 0, CAN_W, CAN_H);
        
        // Neon Sidelines
        ctx.fillStyle = "#1e293b";
        ctx.fillRect(0, 0, 50, CAN_H);
        ctx.fillRect(CAN_W - 50, 0, 50, CAN_H);
        
        ctx.strokeStyle = "#38bdf8";
        ctx.lineWidth = 2;
        ctx.beginPath(); ctx.moveTo(50, 0); ctx.lineTo(50, CAN_H); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(CAN_W - 50, 0); ctx.lineTo(CAN_W - 50, CAN_H); ctx.stroke();

        // Lane markings (Glowing)
        ctx.strokeStyle = "rgba(56, 189, 248, 0.3)";
        ctx.setLineDash([40, 60]);
        ctx.lineDashOffset = -game.current.roadOffset;
        ctx.beginPath(); ctx.moveTo(CAN_W/3, 0); ctx.lineTo(CAN_W/3, CAN_H); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(CAN_W*2/3, 0); ctx.lineTo(CAN_W*2/3, CAN_H); ctx.stroke();
        ctx.setLineDash([]);

        // Sports Bike (Player)
        ctx.save();
        ctx.translate(game.current.player.x, game.current.player.y);
        
        // Main Body
        ctx.fillStyle = "#38bdf8";
        ctx.shadowBlur = 15;
        ctx.shadowColor = "#38bdf8";
        ctx.beginPath();
        ctx.roundRect(5, 10, BW-10, BH-20, 5);
        ctx.fill();
        
        // Seat
        ctx.fillStyle = "#000";
        ctx.fillRect(8, 30, BW-16, 20);
        
        // Handlebars
        ctx.strokeStyle = "#94a3b8";
        ctx.lineWidth = 3;
        ctx.beginPath(); ctx.moveTo(0, 20); ctx.lineTo(BW, 20); ctx.stroke();
        
        // Wheels (Front & Back)
        ctx.fillStyle = "#1e293b";
        ctx.fillRect(10, 0, BW-20, 15);
        ctx.fillRect(10, BH-15, BW-20, 15);
        
        // Neon Accents
        ctx.fillStyle = "#fff";
        ctx.fillRect(BW/2 - 2, 12, 4, 10);
        
        ctx.restore();

        // Obstacles
        game.current.obstacles.forEach(o => {
            ctx.save();
            ctx.shadowBlur = 10;
            ctx.shadowColor = o.color;
            ctx.fillStyle = o.color;
            if (o.type === 'POLICE') {
                ctx.fillRect(o.x, o.y, BW + 10, BH);
                // Police lights
                const flash = Math.sin(Date.now() / 100) > 0;
                ctx.fillStyle = flash ? "#f00" : "#00f";
                ctx.fillRect(o.x + 5, o.y + 5, BW, 10);
            } else {
                ctx.fillRect(o.x, o.y, BW + 10, 30);
                // Stripes on barrier
                ctx.fillStyle = "#fff";
                ctx.fillRect(o.x + 10, o.y, 5, 30);
                ctx.fillRect(o.x + 25, o.y, 5, 30);
            }
            ctx.restore();
        });
    };

    useEffect(() => {
        if (gameStarted) {
            game.current.lastFrame = performance.now();
            if (game.current.lastSpawn === 0) {
                game.current.lastSpawn = performance.now();
            }
            requestAnimationFrame(update);
        }
    }, [gameStarted, update]);

    const handleInput = useCallback((lane) => {
        if (!gameStarted || gameOver) return;
        game.current.player.targetX = 50 + lane * 100 + (100 - BW) / 2;
        setIsBoost(true);
        setTimeout(() => setIsBoost(false), 200);
    }, [gameStarted, gameOver]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (!gameStarted || gameOver) return;
            const currentLane = Math.round((game.current.player.targetX - 75) / 100);
            if (e.key === "ArrowLeft" || e.key === "a") {
                handleInput(Math.max(0, currentLane - 1));
            } else if (e.key === "ArrowRight" || e.key === "d") {
                handleInput(Math.min(2, currentLane + 1));
            }
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [gameStarted, gameOver, handleInput]);

    const restartGame = () => {
        setScore(0);
        setGameOver(false);
        game.current = {
            player: { x: CAN_W/2 - BW/2, y: CAN_H - 150, targetX: CAN_W/2 - BW/2 },
            obstacles: [],
            lastSpawn: performance.now(),
            lastFrame: performance.now(),
            speed: 6,
            roadOffset: 0
        };
        setGameStarted(true);
    };

    const handleTouchStart = (e) => {
        if (!gameStarted || gameOver) return;
        const touch = e.touches[0];
        touchStartRef.current = { x: touch.clientX, y: touch.clientY, active: true };
    };

    const handleTouchMove = (e) => {
        if (!touchStartRef.current.active) return;
        const touch = e.touches[0];
        const dx = touch.clientX - touchStartRef.current.x;
        const threshold = 30; // 30px swipe threshold
        if (Math.abs(dx) > threshold) {
            const currentLane = Math.round((game.current.player.targetX - 75) / 100);
            if (dx < 0) {
                handleInput(Math.max(0, currentLane - 1));
            } else {
                handleInput(Math.min(2, currentLane + 1));
            }
            touchStartRef.current.active = false; // Lock swipe latch
        }
        if (e.cancelable) {
            e.preventDefault();
        }
    };

    const handleTouchEnd = () => {
        touchStartRef.current.active = false;
    };

    return (
        <div 
            className="cb_container"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
        >
            <div className="cb_header">
                <button onClick={onBack} className="cb_back">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6"/></svg>
                    <span>Quit</span>
                </button>
                <div className="cb_score">SCORE: {Math.floor(score)}</div>
            </div>
            <div className="cb_canvas_wrapper">
                <canvas ref={canvasRef} width={CAN_W} height={CAN_H} />
            </div>
            
            <div className="rr_controls">
                <button onClick={() => handleInput(0)} className="control-btn left-btn">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6"/></svg>
                    <span>LEFT</span>
                </button>
                <button onClick={() => handleInput(1)} className="control-btn center-btn">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="3"/></svg>
                    <span>CENTER</span>
                </button>
                <button onClick={() => handleInput(2)} className="control-btn right-btn">
                    <span>RIGHT</span>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6"/></svg>
                </button>
            </div>

            {!gameStarted && !gameOver && (
                <div className="cb_overlay">
                    <h1>CYBER <span>BIKE</span></h1>
                    <p>Dodge obstacles in the neon city! Swipe Left/Right or Tap to steer.</p>
                    <button onClick={() => setGameStarted(true)}>REV ENGINE</button>
                </div>
            )}
            {gameOver && (
                <div className="cb_overlay">
                    <h1>CRASHED! 💥</h1>
                    <p>Total Score: {Math.floor(score)}</p>
                    <button onClick={restartGame}>RETRY</button>
                </div>
            )}
        </div>
    );
};

export default CyberBike;
